import AccountIcon from "@/components/AccountIcon";
import type {
  Award,
  Certification,
  CustomSection,
  CustomSectionGroup,
  Education,
  Experience,
  Interest,
  Language,
  Profile,
  Project,
  Publication,
  Reference,
  SectionWithItem,
  Skill,
  Volunteer,
} from "@/lib/schema/sections";
import { TResume } from "@/lib/store/resume";
import { onPreviewSectionClick } from "@/lib/utils/resumeBuilder";
import { isUrl } from "@/utils";
import { isEmptyString } from "@/utils/preview";
import classNames from "classnames";
import { get } from "lodash";
import { Link as LinkIcon, Mail, MapPin, Phone } from "lucide-react";
import { Fragment } from "react";
import { TemplateProps } from ".";
import { LevelMap } from "../sidebars/left/sections/Languages";
import { Picture } from "../sidebars/left/sections/shared/Picture";

const Header = ({ resume }: { resume: TResume }) => {
  const basics = resume.data.basics;

  if (!basics) return null;

  return (
    <div
      className="flex items-center space-x-4"
      onClick={() =>
        onPreviewSectionClick(`builder-basics`, {
          highlight: false,
          accordion: false,
        })
      }
    >
      <Picture resume={resume} />

      <div className="space-y-0.5">
        <div className="text-2xl font-bold">{basics.name}</div>
        <div className="text-base">{basics.headline}</div>

        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm">
          {basics.location && (
            <div className="flex items-center gap-x-1.5 border-r pr-2 last:border-r-0 last:pr-0">
              <MapPin
                className="resume-icon text-resume-primary"
                strokeWidth={2.5}
                size={16}
              />
              <div>{basics.location}</div>
            </div>
          )}
          {basics.phone && (
            <div className="flex items-center gap-x-1.5 border-r pr-2 last:border-r-0 last:pr-0">
              <Phone
                className="resume-icon text-resume-primary"
                strokeWidth={2.5}
                size={16}
              />
              <a href={`tel:${basics.phone}`} target="_blank" rel="noreferrer">
                {basics.phone}
              </a>
            </div>
          )}
          {basics.email && (
            <div className="flex items-center gap-x-1.5 border-r pr-2 last:border-r-0 last:pr-0">
              <Mail
                className="resume-icon text-resume-primary"
                strokeWidth={2.5}
                size={16}
              />
              <a
                href={`mailto:${basics.email}`}
                target="_blank"
                rel="noreferrer"
              >
                {basics.email}
              </a>
            </div>
          )}
          {basics.customFields?.map((item) => (
            <div
              key={item.id}
              className="flex items-center gap-x-1.5 border-r pr-2 last:border-r-0 last:pr-0"
            >
              <AccountIcon
                name={item.icon}
                className="resume-icon text-resume-primary"
                strokeWidth={2.5}
                size={16}
              />
              <span>{[item.name, item.value].filter(Boolean).join(": ")}</span>
            </div>
          ))}
          {basics.website?.href && (
            <div className="flex items-center gap-x-1.5">
              <LinkIcon
                className="resume-icon text-resume-primary"
                strokeWidth={2.5}
                size={16}
              />
              <a target="_blank" rel="noreferrer">
                {basics.website?.href}
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Summary = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.summary;
  if (!section?.visible || isEmptyString(section?.content)) return null;

  return (
    <section
      id={section?.id}
      className="section-item"
      onClick={() => onPreviewSectionClick(`builder-${section?.id}`)}
    >
      <h4 className="mb-2 border-b pb-0.5 text-lg font-bold">
        {section?.name}
      </h4>

      <div dangerouslySetInnerHTML={{ __html: section?.content || "" }} />
    </section>
  );
};

type RatingProps = { level: number };

const Rating = ({ level }: RatingProps) => (
  <div className="flex items-center gap-x-1.5">
    {Array.from({ length: 5 }).map((_, index) => (
      <div
        key={index}
        className={classNames(
          "border-resume-primary h-2 w-2 rounded-full border",
          level > index && "bg-resume-primary",
        )}
      />
    ))}
  </div>
);

type SectionProps<T> = {
  section?: SectionWithItem<T> | CustomSectionGroup;
  children?: (item: T) => React.ReactNode;
  className?: string;
  urlKey?: keyof T;
  levelKey?: keyof T;
  summaryKey?: keyof T;
  keywordsKey?: keyof T;
};

const Section = <T,>({
  section,
  children,
  className,
  urlKey,
  levelKey,
  summaryKey,
  keywordsKey,
}: SectionProps<T>) => {
  if (!section?.visible || !section?.items?.length) return null;

  return (
    <section
      id={section.id}
      className="section-item grid"
      onClick={() =>
        onPreviewSectionClick(`builder-${section?.id}-${section?.items[0]?.id}`)
      }
    >
      <h4 className="mb-2 border-b pb-0.5 text-lg font-bold">{section.name}</h4>

      <div
        className="grid gap-x-6 gap-y-3"
        style={{ gridTemplateColumns: `repeat(1, 1fr)` }}
      >
        {section.items
          .filter((item) => item.visible)
          .map((item) => {
            const url = urlKey && get(item, urlKey);
            const level = LevelMap[levelKey && get(item, levelKey, "basic")];

            const summary = (summaryKey && get(item, summaryKey, "")) as
              | string
              | undefined;
            const keywords = (keywordsKey && get(item, keywordsKey, [])) as
              | string[]
              | undefined;

            return (
              <div
                key={item.id}
                className={classNames("space-y-2", className)}
                onClick={(e) => {
                  e.stopPropagation();
                  onPreviewSectionClick(`builder-${section?.id}-${item.id}`);
                }}
              >
                {(children?.(item as T) || url?.href) && (
                  <div>
                    {children?.(item as T)}
                    {url?.href && (
                      <a target="_blank" rel="noreferrer" href={url?.href}>
                        {url?.href}
                      </a>
                    )}
                  </div>
                )}

                {summary !== undefined && !isEmptyString(summary) && (
                  <div dangerouslySetInnerHTML={{ __html: summary }} />
                )}

                {level !== undefined && level > 0 && <Rating level={level} />}

                {keywords !== undefined && keywords.length > 0 && (
                  <p className="text-sm">{keywords.join(", ")}</p>
                )}
              </div>
            );
          })}
      </div>
    </section>
  );
};

const Profiles = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.profiles;
  const fontSize = resume.data.settings?.typography.font.size;

  return (
    <Section<Profile> section={section}>
      {(item) => (
        <div>
          {isUrl(item.url?.href) ? (
            <a href={item.url?.href} className="text-resume-primary">
              <img
                className="resume-icon mr-1"
                width={fontSize}
                height={fontSize}
                alt={item.network}
                src={`https://cdn.simpleicons.org/${item.icon}`}
              />
              {item.username}
            </a>
          ) : (
            <p>{item.username}</p>
          )}
          {item.url?.href && (
            <a
              className="block text-sm"
              target="_blank"
              rel="noreferrer"
              href={item.url?.href}
            >
              {item.url?.href}
            </a>
          )}
        </div>
      )}
    </Section>
  );
};

const Experience = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.workExperience;
  return (
    <Section<Experience> section={section} urlKey="url" summaryKey="summary">
      {(item) => (
        <div className="flex items-center justify-between">
          <div className="text-left">
            <div className="text-base font-bold">{item.company}</div>
            <div>{item.position}</div>
          </div>

          <div className="shrink-0 text-right">
            <div className="font-bold">{item.date}</div>
            <div>{item.location}</div>
          </div>
        </div>
      )}
    </Section>
  );
};

const Internship = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.internship;
  return (
    <Section<Experience> section={section} urlKey="url" summaryKey="summary">
      {(item) => (
        <div className="flex items-center justify-between">
          <div className="text-left">
            <div className="text-base font-bold">{item.company}</div>
            <div>{item.position}</div>
          </div>

          <div className="shrink-0 text-right">
            <div className="font-bold">{item.date}</div>
            <div>{item.location}</div>
          </div>
        </div>
      )}
    </Section>
  );
};

const Education = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.education;
  return (
    <Section<Education> section={section} summaryKey="summary">
      {(item) => (
        <div className="flex items-center justify-between">
          <div className="text-left">
            <div className="text-base font-bold">{item.institution}</div>
            <div>{item.major}</div>
            <div>{item.score}</div>
            <div>{item.officialSite}</div>
          </div>

          <div className="shrink-0 text-right">
            <div className="font-bold">{item.date}</div>
            <div>{item.studyType}</div>
          </div>
        </div>
      )}
    </Section>
  );
};

const Awards = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.awards;
  return (
    <Section<Award> section={section} urlKey="url" summaryKey="summary">
      {(item) => (
        <div className="flex items-center justify-between">
          <div className="text-left">
            <div className="text-base font-bold">{item.title}</div>
            <div>{item.awarder}</div>
          </div>

          <div className="shrink-0 text-right">
            <div className="font-bold">{item.date}</div>
          </div>
        </div>
      )}
    </Section>
  );
};

const Certifications = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.certifications;
  return (
    <Section<Certification> section={section} urlKey="url" summaryKey="summary">
      {(item) => (
        <div className="flex items-center justify-between">
          <div className="text-left">
            <div className="text-base font-bold">{item.name}</div>
            <div>{item.issuer}</div>
          </div>

          <div className="shrink-0 text-right">
            <div className="font-bold">{item.date}</div>
          </div>
        </div>
      )}
    </Section>
  );
};

const Skills = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.skills;
  return (
    <Section<Skill> section={section} levelKey="level" keywordsKey="keywords">
      {(item) => (
        <div>
          <div className="text-base font-bold">{item.name}</div>
          <div>{item.description}</div>
        </div>
      )}
    </Section>
  );
};

const Interests = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.interests;
  return (
    <Section<Interest>
      section={section}
      keywordsKey="keywords"
      className="space-y-0.5"
    >
      {(item) => <div className="text-base font-bold">{item.name}</div>}
    </Section>
  );
};

const Publications = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.publications;
  return (
    <Section<Publication> section={section} urlKey="url" summaryKey="summary">
      {(item) => (
        <div className="flex items-center justify-between">
          <div className="text-left">
            <div className="text-base font-bold">{item.name}</div>
            <div>{item.publisher}</div>
          </div>

          <div className="shrink-0 text-right">
            <div className="font-bold">{item.date}</div>
          </div>
        </div>
      )}
    </Section>
  );
};

const Volunteer = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.volunteer;

  return (
    <Section<Volunteer> section={section} urlKey="url" summaryKey="summary">
      {(item) => (
        <div className="flex items-center justify-between">
          <div className="text-left">
            <div className="text-base font-bold">{item.organization}</div>
            <div>{item.position}</div>
          </div>

          <div className="shrink-0 text-right">
            <div className="font-bold">{item.date}</div>
            <div>{item.location}</div>
          </div>
        </div>
      )}
    </Section>
  );
};

const Languages = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.languages;

  return (
    <Section<Language> section={section} levelKey="level">
      {(item) => (
        <div className="space-y-0.5">
          <div className="text-base font-bold">{item.name}</div>
          <div>{item.description}</div>
        </div>
      )}
    </Section>
  );
};

const Projects = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.projects;

  return (
    <Section<Project>
      section={section}
      urlKey="url"
      summaryKey="summary"
      keywordsKey="keywords"
    >
      {(item) => (
        <div className="flex items-center justify-between">
          <div className="text-left">
            <div className="text-base font-bold">{item.name}</div>
            <div>{item.description}</div>
          </div>

          <div className="shrink-0 text-right">
            <div className="font-bold">{item.date}</div>
          </div>
        </div>
      )}
    </Section>
  );
};

const References = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.references;

  return (
    <Section<Reference> section={section} urlKey="url" summaryKey="summary">
      {(item) => (
        <div>
          <div className="text-base font-bold">{item.name}</div>
          <div>{item.description}</div>
        </div>
      )}
    </Section>
  );
};

const Custom = ({ id, resume }: { id: string; resume: TResume }) => {
  const section = resume.data.sections?.custom?.[id];
  return (
    <Section<CustomSection>
      section={section}
      urlKey="url"
      summaryKey="summary"
      keywordsKey="keywords"
    >
      {(item) => {
        if (!item.name && !item.description && !item.date && !item.location) {
          return null;
        }
        return (
          <div className="flex items-center justify-between">
            <div className="text-left">
              <div className="text-base font-bold">{item.name}</div>
              <div>{item.description}</div>
            </div>

            <div className="shrink-0 text-right">
              <div className="font-bold">{item.date}</div>
              <div>{item.location}</div>
            </div>
          </div>
        );
      }}
    </Section>
  );
};

const mapSectionToComponent = (section: any, resume: TResume) => {
  switch (section) {
    case "profiles":
      return <Profiles resume={resume} />;
    case "summary":
      return <Summary resume={resume} />;
    case "workExperience":
      return <Experience resume={resume} />;
    case "internship":
      return <Internship resume={resume} />;
    case "education":
      return <Education resume={resume} />;
    case "awards":
      return <Awards resume={resume} />;
    case "certifications":
      return <Certifications resume={resume} />;
    case "skills":
      return <Skills resume={resume} />;
    case "interests":
      return <Interests resume={resume} />;
    case "publications":
      return <Publications resume={resume} />;
    case "volunteer":
      return <Volunteer resume={resume} />;
    case "languages":
      return <Languages resume={resume} />;
    case "projects":
      return <Projects resume={resume} />;
    case "references":
      return <References resume={resume} />;
    default:
      if (section.startsWith("custom."))
        return <Custom id={section.split(".")[1]} resume={resume} />;

      return null;
  }
};

export const Rhyhorn = ({
  resume,
  columns,
  isFirstPage = false,
}: TemplateProps) => {
  const [main, sidebar] = columns;

  return (
    <div className="space-y-4 p-custom">
      {isFirstPage && <Header resume={resume} />}

      {main.map((section) => (
        <Fragment key={section}>
          {mapSectionToComponent(section, resume)}
        </Fragment>
      ))}

      {sidebar.map((section) => (
        <Fragment key={section}>
          {mapSectionToComponent(section, resume)}
        </Fragment>
      ))}
    </div>
  );
};
